import { useState, useEffect } from 'react';
import { Users, Stethoscope, Syringe, Heart, Baby, Ambulance, X } from 'lucide-react';
import { cn } from '../lib/utils';

// Iconos y colores por rol
const ROLE_CONFIG = {
  medico: {
    icon: Stethoscope,
    color: 'blue',
    bgColor: 'bg-blue-500/20',
    borderColor: 'border-blue-500/50',
    textColor: 'text-blue-300',
    label: 'Médico'
  },
  enfermera: {
    icon: Syringe,
    color: 'green',
    bgColor: 'bg-green-500/20',
    borderColor: 'border-green-500/50',
    textColor: 'text-green-300',
    label: 'Enfermera'
  },
  tens: {
    icon: Heart,
    color: 'purple',
    bgColor: 'bg-purple-500/20',
    borderColor: 'border-purple-500/50',
    textColor: 'text-purple-300',
    label: 'TENS'
  },
  matrona: {
    icon: Baby,
    color: 'pink',
    bgColor: 'bg-pink-500/20',
    borderColor: 'border-pink-500/50',
    textColor: 'text-pink-300',
    label: 'Matrona'
  },
  paramedico: {
    icon: Ambulance,
    color: 'orange',
    bgColor: 'bg-orange-500/20',
    borderColor: 'border-orange-500/50',
    textColor: 'text-orange-300',
    label: 'Paramédico'
  },
  equipo: {
    icon: Users,
    color: 'slate',
    bgColor: 'bg-slate-500/20',
    borderColor: 'border-slate-500/50',
    textColor: 'text-slate-300',
    label: 'Equipo'
  }
};

const TeamNotificationsPanel = ({ notifications = [], onDismiss }) => {
  const [visibleNotifications, setVisibleNotifications] = useState([]);

  useEffect(() => {
    // Filtrar notificaciones válidas y mostrarlas
    const validNotifications = notifications.filter(n => n && n.id);
    setVisibleNotifications(validNotifications);
  }, [notifications]);

  const handleDismiss = (notifId) => {
    setVisibleNotifications(prev => prev.filter(n => n.id !== notifId));
    if (onDismiss) {
      onDismiss(notifId);
    }
  };

  if (visibleNotifications.length === 0) {
    return null;
  }

  return (
    <div 
      className="fixed top-20 right-4 z-50 flex flex-col gap-2 max-w-sm w-full pointer-events-none"
      data-testid="team-notifications-panel"
    >
      {visibleNotifications.map((notification, index) => {
        const roleKey = (notification.role || 'equipo').toLowerCase();
        const config = ROLE_CONFIG[roleKey] || ROLE_CONFIG.equipo;
        const Icon = config.icon;

        return (
          <div
            key={notification.id}
            className={cn(
              "pointer-events-auto",
              "animate-in slide-in-from-right-5 fade-in-0 duration-300",
              "transform transition-all"
            )}
            style={{ animationDelay: `${index * 100}ms` }}
            data-testid={`team-notification-${notification.id}`}
          >
            <div 
              className={cn(
                "flex items-start gap-3 p-3 rounded-lg border backdrop-blur-sm shadow-lg",
                config.bgColor,
                config.borderColor
              )}
            >
              <div className={cn("p-2 rounded-full", config.bgColor)}>
                <Icon size={18} className={config.textColor} />
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className={cn("text-xs font-semibold uppercase", config.textColor)}>
                    {config.label}
                  </span>
                  <span className="text-slate-500 text-xs">
                    {notification.elapsed_seconds !== undefined && 
                      `${Math.floor(notification.elapsed_seconds / 60)}:${(notification.elapsed_seconds % 60).toString().padStart(2, '0')}`
                    }
                  </span>
                </div>
                <p className="text-sm text-white mt-0.5 line-clamp-2">
                  {notification.message || notification.action}
                </p>
              </div>

              <button
                onClick={() => handleDismiss(notification.id)}
                className="p-1 rounded hover:bg-slate-700/50 transition-colors flex-shrink-0"
                aria-label="Cerrar notificación"
              >
                <X size={14} className="text-slate-400" />
              </button>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default TeamNotificationsPanel;
